/**
 * Watched areas (WS-F, workspace docs/guardian/06, 2026-10-23): the counties,
 * cities and points the department monitors for team alerts. Admins add and
 * remove areas; every member sees them read-only. A place is identified by a
 * Census GEOID (5 digits for a county, 7 for a city) — the Guardian app has no
 * admin places search, so the admin types the GEOID and the Backend derives the
 * name. A point is a latitude/longitude with a radius (<= 50 km). Up to 50
 * areas. All names come from the Backend and render as text only. A 503
 * `org_unavailable` hides the section.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { addDepartmentArea, deleteDepartmentArea, fetchDepartmentAreas } from '../api/guardianApi';
import { departmentErrorMessage } from '../utils/departmentErrors';
import { formatDeptTime } from '../utils/departmentOrg';

export const MAX_AREAS = 50;
const MAX_RADIUS_KM = 50;

function geoidError(placeKind, geoid) {
  const digits = placeKind === 'county' ? 5 : 7;
  if (!/^\d+$/.test(geoid)) return 'A GEOID is only digits.';
  if (geoid.length !== digits) return `A ${placeKind} GEOID is ${digits} digits.`;
  return null;
}

function AddArea({ onAdd, disabled }) {
  const [mode, setMode] = useState('place');
  const [placeKind, setPlaceKind] = useState('county');
  const [geoid, setGeoid] = useState('');
  const [lat, setLat] = useState('');
  const [lon, setLon] = useState('');
  const [radius, setRadius] = useState('10');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    setError(null);
    let body;
    if (mode === 'place') {
      const g = geoid.trim();
      const err = geoidError(placeKind, g);
      if (err) { setError(err); return; }
      body = { kind: 'place', placeKind, geoid: g };
    } else {
      const latN = Number(lat);
      const lonN = Number(lon);
      const radN = Number(radius);
      if (!Number.isFinite(latN) || latN < -90 || latN > 90) { setError('Latitude must be between -90 and 90.'); return; }
      if (!Number.isFinite(lonN) || lonN < -180 || lonN > 180) { setError('Longitude must be between -180 and 180.'); return; }
      if (!Number.isFinite(radN) || radN <= 0 || radN > MAX_RADIUS_KM) { setError(`Radius must be between 0 and ${MAX_RADIUS_KM} km.`); return; }
      body = { kind: 'point', latitude: latN, longitude: lonN, radiusKm: radN };
    }
    setBusy(true);
    const err = await onAdd(body);
    setBusy(false);
    if (err) { setError(err); return; }
    setGeoid('');
    setLat('');
    setLon('');
    setRadius('10');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <Select size="small" value={mode} onChange={(e) => setMode(e.target.value)} inputProps={{ 'aria-label': 'Area type' }}>
          <MenuItem value="place">County or city</MenuItem>
          <MenuItem value="point">Point and radius</MenuItem>
        </Select>
        {mode === 'place' ? (
          <>
            <Select size="small" value={placeKind} onChange={(e) => setPlaceKind(e.target.value)} inputProps={{ 'aria-label': 'Place kind' }}>
              <MenuItem value="county">County</MenuItem>
              <MenuItem value="place">City / place</MenuItem>
            </Select>
            <TextField
              size="small"
              label={placeKind === 'county' ? 'County GEOID (5 digits)' : 'Place GEOID (7 digits)'}
              value={geoid}
              onChange={(e) => setGeoid(e.target.value.replace(/\D/g, '').slice(0, 7))}
              inputProps={{ inputMode: 'numeric', 'aria-label': 'GEOID' }}
            />
          </>
        ) : (
          <>
            <TextField size="small" label="Latitude" value={lat} onChange={(e) => setLat(e.target.value)} inputProps={{ inputMode: 'decimal', 'aria-label': 'Latitude' }} sx={{ width: 120 }} />
            <TextField size="small" label="Longitude" value={lon} onChange={(e) => setLon(e.target.value)} inputProps={{ inputMode: 'decimal', 'aria-label': 'Longitude' }} sx={{ width: 120 }} />
            <TextField size="small" label="Radius (km)" value={radius} onChange={(e) => setRadius(e.target.value)} inputProps={{ inputMode: 'decimal', 'aria-label': 'Radius in kilometres' }} sx={{ width: 120 }} />
          </>
        )}
        <Button variant="contained" onClick={submit} disabled={busy || disabled}>Add area</Button>
      </Box>
    </Box>
  );
}

function AreaRow({ area, isAdmin, onDelete }) {
  const coords = area.kind === 'point' && area.latitude != null && area.longitude != null
    ? `${Number(area.latitude).toFixed(3)}, ${Number(area.longitude).toFixed(3)} · ${area.radiusKm} km`
    : null;
  return (
    <Paper variant="outlined" component="li" sx={{ p: 1.5, listStyle: 'none', display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', justifyContent: 'space-between' }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>{area.name || area.geoid || 'Area'}</Typography>
        <Chip size="small" variant="outlined" label={area.kind === 'point' ? 'Point' : (area.placeKind === 'county' ? 'County' : 'City')} />
        {area.state && <Chip size="small" variant="outlined" label={area.state} />}
        {coords && <Typography variant="caption" color="text.secondary">{coords}</Typography>}
        {formatDeptTime(area.createdAt) && (
          <Typography variant="caption" color="text.secondary">Added {formatDeptTime(area.createdAt)}</Typography>
        )}
      </Box>
      {isAdmin && (
        <Button size="small" color="error" onClick={() => onDelete(area)}>Remove</Button>
      )}
    </Paper>
  );
}

/**
 * @param {object} props
 * @param {string|null} props.authToken
 * @param {boolean} [props.isAdmin]
 */
export default function DepartmentAreas({ authToken, isAdmin = false }) {
  const [areas, setAreas] = useState([]);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);
  const [off, setOff] = useState(false);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const load = useCallback(async () => {
    if (!authToken) return;
    setStatus('loading');
    const res = await fetchDepartmentAreas(authToken);
    if (!alive.current) return;
    if (res?.success) {
      setAreas(Array.isArray(res.data?.areas) ? res.data.areas : []);
      setStatus('ready');
    } else if (res?.data?.code === 'org_unavailable') {
      setOff(true);
    } else {
      setStatus('error');
      setError(departmentErrorMessage(res, 'Could not load watched areas.'));
    }
  }, [authToken]);
  useEffect(() => { load(); }, [load]);

  const onAdd = async (body) => {
    setError(null);
    const res = await addDepartmentArea(authToken, body);
    if (res?.success) { load(); return null; }
    return departmentErrorMessage(res, 'Could not add the area.');
  };

  const onDelete = async (area) => {
    setError(null);
    const res = await deleteDepartmentArea(authToken, area.id);
    if (res?.success) { load(); return; }
    setError(departmentErrorMessage(res, 'Could not remove the area.'));
  };

  if (off) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography variant="h6" component="h2">Watched areas</Typography>
      <Typography variant="body2" color="text.secondary">
        {isAdmin
          ? 'The counties, cities and points your department monitors for fire-weather and fire alerts.'
          : 'The counties, cities and points your department monitors for fire-weather and fire alerts.'}
      </Typography>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      {isAdmin && <AddArea onAdd={onAdd} disabled={areas.length >= MAX_AREAS} />}
      {isAdmin && areas.length >= MAX_AREAS && (
        <Typography variant="caption" color="text.secondary">You have reached the limit of {MAX_AREAS} areas. Remove one to add another.</Typography>
      )}

      {status === 'loading' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} role="status">
          <CircularProgress size={18} />
          <Typography variant="body2">Loading areas…</Typography>
        </Box>
      )}

      {status === 'ready' && (
        areas.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No areas yet.</Typography>
        ) : (
          <Box component="ul" sx={{ m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {areas.map((a) => <AreaRow key={a.id} area={a} isAdmin={isAdmin} onDelete={onDelete} />)}
          </Box>
        )
      )}
    </Paper>
  );
}
